import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { RoutesPaths } from "../Router/constant";
import DgiLogo from "../Pages/Auth/DgiLogo";
import {
  AuditIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  HomeIcon,
  IdCardIcon,
  NetworkIcon,
  PlugIcon,
  ReportIcon,
  SettingsIcon,
  ShieldCheckIcon,
  ShieldIcon,
  UserIcon,
  UsersIcon,
} from "./Icons";
import "./Sidebar.css";

// `children` open a submenu; `to` links straight to a route. Items with
// neither are placeholders for sections that are not built yet.
const NAV_ITEMS = [
  { label: "Dashboard", icon: HomeIcon, to: RoutesPaths.DASHBOARD },
  { label: "Taxpayers", icon: UsersIcon, to: RoutesPaths.TAXPAYERS },
  {
    label: "Data Quality",
    icon: ShieldCheckIcon,
    children: [
      { label: "Overview", to: RoutesPaths.DATA_QUALITY },
      { label: "Duplicate Records", to: RoutesPaths.DQ_DUPLICATES },
    ],
  },
  { label: "Risk & Compliance", icon: ShieldIcon, to: RoutesPaths.RISK },
  {
    label: "Relationship Analysis",
    icon: NetworkIcon,
    to: RoutesPaths.RELATIONSHIPS,
  },
  { label: "Audit Intelligence", icon: AuditIcon, to: RoutesPaths.AUDIT },
  {
    label: "NIF Registration",
    icon: IdCardIcon,
    children: [{ label: "Register New NIF", to: RoutesPaths.NIF_REGISTRATION }],
  },
  {
    label: "Data Integration",
    icon: PlugIcon,
    children: [
      { label: "Data Sources", to: RoutesPaths.DI_SOURCES },
      { label: "Import Data", to: RoutesPaths.DI_IMPORT },
      { label: "Synchronization", to: RoutesPaths.DI_SYNC },
    ],
  },
  { label: "Reports", icon: ReportIcon, to: RoutesPaths.REPORTS },
  { label: "User Management", icon: UsersIcon, to: RoutesPaths.USER_MANAGEMENT },
  { label: "Security & Logs", icon: ShieldIcon, to: RoutesPaths.SECURITY },
  { label: "Settings", icon: SettingsIcon, to: RoutesPaths.SETTINGS },
];

export default function Sidebar({ open }) {
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(null);

  // Open the section that owns the current route.
  useEffect(() => {
    if (pathname.startsWith(RoutesPaths.TAXPAYERS)) setExpanded("Taxpayers");
    else if (pathname.startsWith(RoutesPaths.DATA_QUALITY)) {
      setExpanded("Data Quality");
    } else if (pathname.startsWith(RoutesPaths.RISK)) {
      setExpanded("Risk & Compliance");
    } else if (pathname.startsWith(RoutesPaths.RELATIONSHIPS)) {
      setExpanded("Relationship Analysis");
    } else if (pathname.startsWith(RoutesPaths.NIF_REGISTRATION)) {
      setExpanded("NIF Registration");
    } else if (pathname.startsWith("/data-integration")) {
      setExpanded("Data Integration");
    }
  }, [pathname]);

  const toggle = (label) =>
    setExpanded((prev) => (prev === label ? null : label));

  return (
    <aside className={`sidebar${open ? "" : " sidebar--collapsed"}`}>
      <div className="sidebar__brand">
        <DgiLogo width={44} />
        <div>
          <p className="sidebar__brand-name">DGI</p>
          <p className="sidebar__brand-sub">Smart Tax Registry</p>
        </div>
      </div>

      <nav className="sidebar__nav">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;

          if (item.to) {
            return (
              <NavLink
                key={item.label}
                to={item.to}
                className={({ isActive }) =>
                  `sidebar__item${isActive ? " sidebar__item--active" : ""}`
                }
              >
                <Icon size={19} />
                <span className="sidebar__item-label">{item.label}</span>
              </NavLink>
            );
          }

          const isGroup = Array.isArray(item.children);
          const isOpen = expanded === item.label;
          const sectionActive =
            (item.label === "Taxpayers" &&
              pathname.startsWith(RoutesPaths.TAXPAYERS)) ||
            (item.label === "Data Quality" &&
              pathname.startsWith(RoutesPaths.DATA_QUALITY)) ||
            (item.label === "Risk & Compliance" &&
              pathname.startsWith(RoutesPaths.RISK)) ||
            (item.label === "Relationship Analysis" &&
              pathname.startsWith(RoutesPaths.RELATIONSHIPS)) ||
            (item.label === "NIF Registration" &&
              pathname.startsWith(RoutesPaths.NIF_REGISTRATION)) ||
            (item.label === "Data Integration" &&
              pathname.startsWith("/data-integration"));

          return (
            <div key={item.label}>
              <button
                type="button"
                className={`sidebar__item${
                  sectionActive ? " sidebar__item--active" : ""
                }`}
                onClick={() => isGroup && toggle(item.label)}
                aria-expanded={isGroup ? isOpen : undefined}
              >
                <Icon size={19} />
                <span className="sidebar__item-label">{item.label}</span>
                {isGroup && (
                  <span className="sidebar__item-caret">
                    {isOpen ? (
                      <ChevronDownIcon size={16} />
                    ) : (
                      <ChevronRightIcon size={16} />
                    )}
                  </span>
                )}
              </button>

              {isGroup && isOpen && item.children.length > 0 && (
                <div className="sidebar__submenu">
                  {item.children.map((child) =>
                    child.to ? (
                      <NavLink
                        key={child.label}
                        to={child.to}
                        end
                        className={({ isActive }) =>
                          `sidebar__subitem${
                            isActive ? " sidebar__subitem--active" : ""
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ) : (
                      <button
                        key={child.label}
                        type="button"
                        className="sidebar__subitem"
                      >
                        {child.label}
                      </button>
                    ),
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="sidebar__user">
        <span className="sidebar__avatar">
          <UserIcon size={20} />
        </span>
        <div className="sidebar__user-meta">
          <p className="sidebar__user-name">John Doe</p>
          <p className="sidebar__user-role">Super Administrator</p>
          <p className="sidebar__user-status">
            <span className="sidebar__dot" />
            Online
          </p>
        </div>
        <ChevronDownIcon size={16} />
      </div>
    </aside>
  );
}
